import { Chip, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';

const ListItem = styled('li')(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

export type DateRangeFilter = {
  startedDate: string | null;
  endDate: string | null;
};

type DateRangeChipOption = {
  key: string;
  labelId: string;
  defaultLabel: string;
  getRange: () => DateRangeFilter;
};

const getStartOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getEndOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const getStartOfWeek = (date: Date): Date => {
  const dayOfWeek = date.getDay();
  const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(date);
  monday.setDate(date.getDate() - diffToMonday);
  return getStartOfDay(monday);
};

export const getThisMonthDateRange = (): DateRangeFilter => {
  const now = new Date();
  const firstDayOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  return {
    startedDate: getStartOfDay(firstDayOfThisMonth).toISOString(),
    endDate: getEndOfDay(now).toISOString(),
  };
};

const DATE_RANGE_CHIP_OPTIONS: DateRangeChipOption[] = [
  {
    key: 'last180days',
    labelId: 'dateRangeChips.last180days',
    defaultLabel: 'Son 180 gün',
    getRange: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 180);
      return {
        startedDate: getStartOfDay(start).toISOString(),
        endDate: getEndOfDay(now).toISOString(),
      };
    },
  },
  {
    key: 'last90days',
    labelId: 'dateRangeChips.last90days',
    defaultLabel: 'Son 90 gün',
    getRange: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 90);
      return {
        startedDate: getStartOfDay(start).toISOString(),
        endDate: getEndOfDay(now).toISOString(),
      };
    },
  },
  {
    key: 'lastMonth',
    labelId: 'dateRangeChips.lastMonth',
    defaultLabel: 'Geçen Ay',
    getRange: () => {
      const now = new Date();
      const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        startedDate: getStartOfDay(firstDayOfLastMonth).toISOString(),
        endDate: getEndOfDay(lastDayOfLastMonth).toISOString(),
      };
    },
  },
  {
    key: 'thisMonth',
    labelId: 'dateRangeChips.thisMonth',
    defaultLabel: 'Bu Ay',
    getRange: getThisMonthDateRange,
  },
  {
    key: 'lastWeek',
    labelId: 'dateRangeChips.lastWeek',
    defaultLabel: 'Geçen Hafta',
    getRange: () => {
      const startOfThisWeek = getStartOfWeek(new Date());
      const startOfLastWeek = new Date(startOfThisWeek);
      const endOfLastWeek = new Date(startOfThisWeek);
      startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);
      endOfLastWeek.setDate(startOfThisWeek.getDate() - 1);
      return {
        startedDate: startOfLastWeek.toISOString(),
        endDate: getEndOfDay(endOfLastWeek).toISOString(),
      };
    },
  },
  {
    key: 'thisWeek',
    labelId: 'dateRangeChips.thisWeek',
    defaultLabel: 'Bu Hafta',
    getRange: () => {
      const now = new Date();
      return {
        startedDate: getStartOfWeek(now).toISOString(),
        endDate: getEndOfDay(now).toISOString(),
      };
    },
  },
  {
    key: 'yesterday',
    labelId: 'dateRangeChips.yesterday',
    defaultLabel: 'Dün',
    getRange: () => {
      const now = new Date();
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      return {
        startedDate: getStartOfDay(yesterday).toISOString(),
        endDate: getEndOfDay(yesterday).toISOString(),
      };
    },
  },
  {
    key: 'today',
    labelId: 'dateRangeChips.today',
    defaultLabel: 'Bugün',
    getRange: () => {
      const now = new Date();
      return {
        startedDate: getStartOfDay(now).toISOString(),
        endDate: getEndOfDay(now).toISOString(),
      };
    },
  },
  {
    key: 'all',
    labelId: 'dateRangeChips.all',
    defaultLabel: 'Hepsi',
    getRange: () => ({
      startedDate: null,
      endDate: null,
    }),
  },
];

type DateRangeChipsArrayProps = {
  selectedChipKey: string;
  onChipChange: (key: string, range: DateRangeFilter) => void;
};

const DateRangeChipsArray = ({ selectedChipKey, onChipChange }: DateRangeChipsArrayProps) => {
  return (
    <Paper
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        listStyle: 'none',
        py: 1,
        px: 0.5,
        m: 0,
        mt: 1,
        borderRadius: '8px',
        width: { xs: '100vw', md: 'calc(100vw - 308px)' },
      }}
      component="ul">
      {DATE_RANGE_CHIP_OPTIONS.map((option) => (
        <ListItem key={option.key}>
          <Chip
            color={selectedChipKey === option.key ? 'primary' : 'default'}
            label={<FormattedMessage id={option.labelId} defaultMessage={option.defaultLabel} />}
            onClick={() => onChipChange(option.key, option.getRange())}
          />
        </ListItem>
      ))}
    </Paper>
  );
};

export default DateRangeChipsArray;
